// ticket-file-persistence.ts
//
// Single source that WRITES a ticket JSON to `.avatar/tickets/` (the
// git-versioned source supervisor-ticket-projection.ts re-reads every
// tick), plus the sibling helper that picks a fresh `T-NNNN` id for a
// worker "create" proposal. Split out of supervisor-proposal-application.ts
// (phase 07, D12) so directive-redirect-application.ts can reuse the SAME
// write path instead of only mutating in-memory tickets — before this
// split, a redirect's `amend`/`create` changed `currentTickets` in memory
// and committed a journal event, but never touched `.avatar/tickets/*.json`
// (F2, dogfood ME-2026-4f0e72: `revision:1` on disk after 2 amends).
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { ensureDir } from "../filesystem-helpers.js";
import { isControllerReservedTicketId } from "./controller-reserved-ticket-ids.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { findPolicyDagWarnings, findStructuralDagError } from "./ticket-dag-validator.js";
import { ticketFileName, ticketIdOfFileName } from "./ticket-file-name.js";

export type CheckTicketDagResult = { ok: true } | { ok: false; reason: string };

/**
 * R-25 (D2): kiểm DAG của MỘT LÔ ticket đã áp trong bộ nhớ, TRƯỚC khi flush
 * ra file — 1 chỗ dùng chung cho done-path (supervisor-proposal-
 * application.ts) và redirect-path (directive-redirect-application.ts) nên
 * hai đường không lặp lại luật (DRY, Req 2).
 *
 * Luật 1-2 (dep không tồn tại / vòng) chặn cứng cả lô, KHÔNG file nào được
 * ghi. Luật 3-4 (thiếu testcase / thiếu verification recipe) chỉ cảnh báo
 * qua `notify` — lô vẫn được áp, vì fixture/thói quen hiện tại chưa tuân cả
 * hai luật này (D2, tránh đốt retry cho plan hợp lệ theo thói quen cũ).
 */
export function checkTicketDagForBatch(
  tickets: Ticket[],
  notify?: (line: string) => void,
): CheckTicketDagResult {
  const structuralError = findStructuralDagError(tickets);
  if (structuralError !== undefined) {
    return { ok: false, reason: `ticket_dag_invalid: ${structuralError}` };
  }
  for (const warning of findPolicyDagWarnings(tickets)) {
    notify?.(`Cảnh báo DAG ticket: ${warning}`);
  }
  return { ok: true };
}

/**
 * Next unused `T-NNNN` id, one above the highest numeric suffix seen among
 * `tickets` — skipping EVERY controller-reserved id (backlog P03: the old
 * version only skipped `REVIEW_CANDIDATE_TICKET_ID`/T-9001, so a plan that
 * grew to T-9002 collided with `CLEAN_REVIEW_CANDIDATE_TICKET_ID` and was
 * rejected by the fail-closed guard in proposal-applier.ts — safe, but the
 * mission just got stuck instead of numbering past the reserved range).
 *
 * P20: `REVIEW_CANDIDATE_TICKET_ID` never appears in `.avatar/tickets/`
 * (review-candidate-session.ts dispatches it from memory), so it is invisible
 * to the max below. Journal events and attempt state are keyed by ticket id,
 * so a plan that grew all the way to a reserved number would silently share
 * both with a controller session. Skipping every reserved id keeps
 * plan-authored ids in their normal low range instead of pushing every
 * mission's numbering above the reservation.
 */
export function nextTicketId(tickets: Ticket[]): string {
  let max = 0;
  for (const t of tickets) {
    const n = Number(t.id.slice(2));
    if (Number.isFinite(n) && n > max) max = n;
  }
  let next = `T-${String(max + 1).padStart(4, "0")}`;
  while (isControllerReservedTicketId(next)) {
    max += 1;
    next = `T-${String(max + 1).padStart(4, "0")}`;
  }
  return next;
}

export interface WriteTicketFileResult {
  /** Repo-relative path of the file just written. */
  written: string;
  /** Repo-relative paths of stale siblings for the SAME id that were removed (title rename). */
  removed: string[];
}

/**
 * Writes one ticket's canonical JSON file under `<repoDir>/.avatar/tickets/`.
 * The file name carries the title slug (ticket-file-name.ts), so an amend
 * that renames the ticket would otherwise leave TWO files for one id — and
 * `loadTicketsFromRepo` loads every `*.json`, so the stale revision would
 * shadow the new one (re-review 2026-09-26). Stale siblings are removed here;
 * callers that stage by path commit `removed` too so the rename lands in git.
 */
export async function writeTicketFile(
  repoDir: string,
  ticket: Ticket,
): Promise<WriteTicketFileResult> {
  const dir = join(repoDir, ".avatar", "tickets");
  await ensureDir(dir);
  const name = ticketFileName(ticket);
  const removed: string[] = [];
  // Files only: a directory that happens to carry a ticket-like name is not
  // ours to delete (code-review 260926 — `fs.rm` without `recursive` threw).
  for (const entry of await fs.readdir(dir, { withFileTypes: true })) {
    if (!entry.isFile() || entry.name === name) continue;
    if (ticketIdOfFileName(entry.name) !== ticket.id) continue;
    await fs.rm(join(dir, entry.name), { force: true });
    removed.push(join(".avatar", "tickets", entry.name));
  }
  await fs.writeFile(join(dir, name), `${JSON.stringify(ticket, null, 2)}\n`, "utf8");
  return { written: join(".avatar", "tickets", name), removed };
}
